import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Globe, Check, ChevronDown } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * LanguageSelector - Mystical language selection dropdown
 * Uses EN/ES text labels instead of flags for international neutrality
 */
export const LanguageSelector = ({ variant = 'default' }) => {
    const { language, setLanguage, currentLanguage, supportedLanguages, t } = useLanguage();
    const [isOpen, setIsOpen] = useState(false);

    const toggleDropdown = () => setIsOpen(!isOpen);
    const selectLanguage = (code) => {
        setLanguage(code);
        setIsOpen(false);
    };

    // Compact variant for header/nav
    if (variant === 'compact') {
        return (
            <div className="relative">
                <button
                    onClick={toggleDropdown}
                    data-testid="language-selector-btn"
                    className="flex items-center gap-2 px-3 py-2 rounded-lg transition-all"
                    style={{
                        background: 'rgba(5,5,5,0.8)',
                        border: '1px solid rgba(212,175,55,0.3)'
                    }}
                >
                    <span className="text-sm font-cinzel font-semibold text-primary">{currentLanguage.label}</span>
                    <ChevronDown 
                        className={`w-4 h-4 text-primary/60 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
                    />
                </button>

                <AnimatePresence>
                    {isOpen && (
                        <motion.div
                            className="absolute top-full right-0 mt-2 rounded-xl overflow-hidden z-50"
                            style={{
                                background: 'linear-gradient(145deg, rgba(5,5,5,0.98) 0%, rgba(10,10,12,1) 100%)',
                                border: '1px solid rgba(212,175,55,0.3)',
                                boxShadow: '0 10px 40px rgba(0,0,0,0.6)',
                                minWidth: '180px'
                            }}
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                        >
                            {supportedLanguages.map((lang) => (
                                <button
                                    key={lang.code}
                                    onClick={() => selectLanguage(lang.code)}
                                    className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                                        language === lang.code 
                                            ? 'bg-primary/10' 
                                            : 'hover:bg-primary/5'
                                    }`}
                                >
                                    <span className="text-sm font-cinzel font-bold text-primary w-8">{lang.label}</span>
                                    <span className="flex-1 text-sm font-inter text-foreground/90">
                                        {lang.nativeName}
                                    </span>
                                    {language === lang.code && (
                                        <Check className="w-4 h-4 text-primary" />
                                    )}
                                </button>
                            ))}
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        );
    }

    // Full variant for settings/welcome screen
    return (
        <div className="relative w-full max-w-xs">
            <button
                onClick={toggleDropdown}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl transition-all"
                style={{
                    background: 'linear-gradient(145deg, rgba(5,5,5,0.95) 0%, rgba(10,10,12,0.98) 100%)',
                    border: '1px solid rgba(212,175,55,0.3)',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
                }}
            >
                <div className="flex items-center gap-3">
                    <Globe className="w-5 h-5 text-primary/60" />
                    <span className="text-sm font-cinzel font-bold text-primary">{currentLanguage.label}</span>
                    <span className="text-sm font-inter text-foreground/80">
                        {currentLanguage.nativeName}
                    </span>
                </div>
                <ChevronDown 
                    className={`w-5 h-5 text-primary/60 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                />
            </button>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        className="absolute top-full left-0 right-0 mt-2 rounded-xl overflow-hidden z-50"
                        style={{
                            background: 'linear-gradient(145deg, rgba(5,5,5,0.98) 0%, rgba(10,10,12,1) 100%)',
                            border: '1px solid rgba(212,175,55,0.3)',
                            boxShadow: '0 10px 40px rgba(0,0,0,0.6)'
                        }}
                        initial={{ opacity: 0, y: -10, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -10, scale: 0.98 }}
                    >
                        <div className="px-4 py-2 border-b border-primary/10">
                            <span className="text-[10px] font-cinzel text-primary/50 uppercase tracking-widest">
                                {t('language_selector')}
                            </span>
                        </div>
                        
                        {supportedLanguages.map((lang) => (
                            <motion.button
                                key={lang.code}
                                onClick={() => selectLanguage(lang.code)}
                                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                                    language === lang.code 
                                        ? 'bg-primary/10' 
                                        : 'hover:bg-primary/5'
                                }`}
                                whileHover={{ x: 4 }}
                            >
                                <span className="text-sm font-cinzel font-bold text-primary w-8">{lang.label}</span>
                                <div className="flex-1">
                                    <p className="text-sm font-inter text-foreground/90">
                                        {lang.nativeName}
                                    </p>
                                    <p className="text-[10px] font-inter text-foreground/40">
                                        {lang.name}
                                    </p>
                                </div>
                                {language === lang.code && (
                                    <motion.div
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                    >
                                        <Check className="w-5 h-5 text-primary" />
                                    </motion.div>
                                )}
                            </motion.button>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Backdrop to close */}
            {isOpen && (
                <div 
                    className="fixed inset-0 z-40" 
                    onClick={() => setIsOpen(false)}
                />
            )}
        </div>
    );
};

export default LanguageSelector;
